import { useCallback, useMemo, useState } from 'react'
import {
    CompanyCsSearchResultDto,
    CompanyEntityFilter,
} from '../../../api/company/models/companyCs/CompanyCsDtos'
import Button from '../../common/form/inputs/Button'
import Modal from '../../common/modals/Modal'
import CsCompanySearch from './CsCompanySearch'

type Props = {
    onClose: () => void
    onConfirm: (companyIds: string[]) => Promise<void> | void
    onConfirmCompanies?: (
        companies: CompanyCsSearchResultDto[],
    ) => Promise<void> | void
    enforcedCompanyEntityFilter?: Extract<
        CompanyEntityFilter,
        'hasDataProviderId' | 'noDataProviderId'
    >
    multiSelect?: boolean
}

export default function CompanySearchModal({
    onClose,
    onConfirm,
    onConfirmCompanies,
    enforcedCompanyEntityFilter,
    multiSelect = true,
}: Props) {
    const [selectedCompanies, setSelectedCompanies] = useState<
        Map<string, CompanyCsSearchResultDto>
    >(new Map())
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | undefined>()

    const selectedIds = useMemo(
        () => Array.from(selectedCompanies.keys()),
        [selectedCompanies],
    )
    const selectedList = useMemo(
        () => Array.from(selectedCompanies.values()),
        [selectedCompanies],
    )

    const toggleSelection = useCallback(
        (company: CompanyCsSearchResultDto) => {
            setSelectedCompanies((prev) => {
                const next = new Map(prev)
                if (!multiSelect) {
                    if (next.has(company.id)) {
                        next.delete(company.id)
                    } else {
                        next.clear()
                        next.set(company.id, company)
                    }
                    return next
                }
                if (next.has(company.id)) {
                    next.delete(company.id)
                } else {
                    next.set(company.id, company)
                }
                return next
            })
        },
        [multiSelect],
    )

    const handleRemove = useCallback((companyId: string) => {
        setSelectedCompanies((prev) => {
            const next = new Map(prev)
            next.delete(companyId)
            return next
        })
    }, [])

    const handleConfirm = useCallback(async () => {
        if (selectedIds.length === 0) {
            setError('Select at least one company to continue.')
            return
        }
        setSubmitting(true)
        setError(undefined)
        try {
            await onConfirm(selectedIds)
            if (onConfirmCompanies) {
                await onConfirmCompanies(selectedList)
            }
        } catch (e: any) {
            setError(e?.message || 'Failed to add companies')
        } finally {
            setSubmitting(false)
        }
    }, [selectedIds, selectedList, onConfirm, onConfirmCompanies])

    return (
        <Modal title="Find Company (CS)" onClose={onClose} size="large">
            <div className="flex flex-col gap-4">
                {selectedList.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <div className="text-sm font-semibold text-gray-700">
                            {multiSelect
                                ? `Selected companies (${selectedList.length})`
                                : 'Selected company'}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {selectedList.map((company) => (
                                <span
                                    key={company.id}
                                    className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-1 text-sm text-sky-800"
                                >
                                    <span>{company.name || company.id}</span>
                                    <button
                                        type="button"
                                        className="text-xs uppercase tracking-wide text-sky-900 hover:text-sky-600"
                                        onClick={() => handleRemove(company.id)}
                                        disabled={submitting}
                                    >
                                        Remove
                                    </button>
                                </span>
                            ))}
                        </div>
                        <div>
                            <Button
                                type="button"
                                style="secondary"
                                onClick={() => setSelectedCompanies(new Map())}
                                enabled={!submitting}
                            >
                                Clear all
                            </Button>
                        </div>
                    </div>
                )}

                {error && <div className="text-red-600">{error}</div>}

                <CsCompanySearch
                    allowEmptyQuery={true}
                    minQueryLength={0}
                    enableUrlSync={false}
                    enforcedCompanyEntityFilter={enforcedCompanyEntityFilter}
                    onSelect={toggleSelection}
                    selectedCompanyIds={selectedIds}
                    multiSelect={multiSelect}
                    showLogoColumn={true}
                    tableWrapperClassName="max-h-[420px] overflow-y-auto overflow-x-auto border rounded"
                    searchPlaceholder="Search for a company (e.g. Contxt)"
                    emptyStateMessage="No companies found"
                />

                <div className="flex items-center justify-between">
                    <Button
                        type="button"
                        style="secondary"
                        onClick={onClose}
                        enabled={!submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        style="primary"
                        onClick={handleConfirm}
                        enabled={!submitting && selectedIds.length > 0}
                    >
                        {submitting
                            ? 'Adding…'
                            : selectedIds.length > 1
                              ? `Add ${selectedIds.length} companies`
                              : 'Add company'}
                    </Button>
                </div>
            </div>
        </Modal>
    )
}
